import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { withTranslation } from 'react-i18next';

import { createTask, updateTask, moveTask } from 'actions/tasks';
import { showModal } from 'actions/modal';

import { createAlert } from 'services/alert-service';
import { trackEvent } from 'services/events-tracker-service';
import { isDependant } from 'utils/task';

import { useCanUser } from 'shared-hooks/permission';

import TaskListItem from 'components/TaskListItem';
import CanUser from 'components/can-user';
import MilestoneTasksNav from 'components/MilestoneTasksNav';
import CreateListTaskItem from 'components/CreateListTaskItem';

import IconSubtask from 'icons/icon-subtask';
import IconTransfer from 'icons/icon-transfer';
import IconArrowDown from 'icons/icon-arrow-down';
import IconArrowUp from 'icons/icon-arrow-up';

const Task = ({ id, name, completed, due_date, projectId, task_group, total_subtasks, completed_subtasks, completed_on, completed_by, onComplete, onUncomplete, onClick, onTaskGroupSelect }) => {
    const dependantOn = isDependant(id, true);

    return (
        <TaskListItem
            key={id}
            id={id}
            name={name}
            completed={completed}
            showAssignees={true}
            showDueDateWhenEmpty={true}
            withTaskGroupIcon={false}
            dueDate={due_date}
            taskGroup={task_group}
            projectId={projectId}
            subtasksCount={total_subtasks}
            completedSubtasksCount={completed_subtasks}
            completedOn={completed && completed_on}
            completedBy={completed && completed_by}
            locked={Boolean(!completed && dependantOn)}
            showComplete={Boolean(onComplete)}
            onComplete={onComplete}
            onUncomplete={onUncomplete}
            onTaskGroupSelect={!dependantOn && onTaskGroupSelect}
            onClick={onClick}
        />
    )
}

const MilestoneTasks = ({ id, projectId, taskGroups, openTasks, completedTasks, onTaskClick, t }) => {
    const [visibleGroup, setVisibleGroup] = useState('open');
    const canEditTask = useCanUser('editTask');
    const canMoveTask = useCanUser('moveTask');

    const preferedTaskGroupId = useMemo(() => {
        const taskGroup = taskGroups.find(taskGroup => taskGroup.milestone === id) || taskGroups.find(taskGroup => !taskGroup.milestone);

        return taskGroup?.id;
    }, [taskGroups, id]);

    const taskProps = useMemo(() => {
        const taskGroupId = preferedTaskGroupId;

        return {
            milestone: id,
            project: projectId,
            task_group: taskGroupId
        };
    }, [preferedTaskGroupId, id, projectId]);

    const createTaskList = useCallback(() => {
        return new Promise((resolve, reject) => {
            showModal('CREATE_TASK_LIST', {
                projectId: projectId,
                onSuccess: resolve,
                onCancel: reject
            })
        });
    }, [projectId]);

    const handleTaskUncomplete = useCallback((taskId, taskGroupId) => {
        updateTask(taskId, {
            completed: false,
            task_group: taskGroupId
        });
    }, []);

    const handleTaskComplete = useCallback(taskId => {
        updateTask(taskId, {
            completed: true,
            completed_on: new Date()
        });
    }, []);

    const handleTaskTaskGroupSelect = useCallback((taskId, taskGroupId, nextTaskProjectId) => {
        if (nextTaskProjectId !== projectId) {
            updateTask(taskId, {
                project: nextTaskProjectId,
                assignees: [],
                milestone: null,
                order: Number.POSITIVE_INFINITY
            }, true);
        }

        moveTask(taskId, {
            type: 'task_group',
            id: taskGroupId
        })
    }, [projectId]);

    const handleTaskBeforeCreate = useCallback(async taskProps => {
        if (preferedTaskGroupId) {
            return taskProps;
        }

        try {
            const taskGroup = await createTaskList();
            return {
                ...taskProps,
                task_group: taskGroup?.id
            };
        } catch (e) {}

        return false;
    }, [preferedTaskGroupId]);

    const handleTaskCreate = useCallback(() => {
        trackEvent('TASK.CREATE', {
            via: 'Milestone Single'
        });
    }, []);

    const handleVisibleGroupChange = useCallback(id => {
        setVisibleGroup(id)
    }, [setVisibleGroup]);

    useEffect(() => {
        if (completedTasks.length === 0) {
            setVisibleGroup('open');
        }

    }, [completedTasks.length, setVisibleGroup]);

    return (
        <React.Fragment>
            <div className="content-panel-nav-holder">
                <MilestoneTasksNav openTasksCount={openTasks.length} completedTasksCount={completedTasks.length} current={visibleGroup} onClick={handleVisibleGroupChange} />
            </div>

            <div className="content-panel-section">

                <div className="section-head section-head-double">
                    <div className="head-item">
                        <h4>
                            <span className="title-icon"><IconSubtask /></span>

                            <span className="text">{ t('task:Tasks') }</span>
                        </h4>
                    </div>

                    <div className="head-item">
                        <h4>
                            <span className="title-icon"><IconTransfer /></span>

                            <span className="text">{ t('task:Status') }</span>
                        </h4>
                    </div>
                </div>

                <div className="content-panel-subtasks">
                    {
                        visibleGroup === 'open' ? (
                            <div className="subtasks">
                                {
                                    openTasks.map(task => (
                                        <Task
                                            key={task.id}
                                            id={task.id}
                                            name={task.name}
                                            completed={task.completed}
                                            due_date={task.due_date}
                                            projectId={task.project}
                                            task_group={task.task_group}
                                            total_subtasks={task.total_subtasks}
                                            completed_on={task.completed_on}
                                            completed_by={task.completed_by}
                                            onUncomplete={canEditTask && handleTaskUncomplete}
                                            onComplete={canEditTask && handleTaskComplete}
                                            onTaskGroupSelect={canMoveTask && handleTaskTaskGroupSelect}
                                            onClick={onTaskClick}
                                        />
                                    ))
                                }

                                <CanUser type="createTask">
                                    <CreateListTaskItem taskProps={taskProps} onBeforeCreate={handleTaskBeforeCreate} onCreate={handleTaskCreate} />
                                </CanUser>
                            </div>
                        ) : (
                            <div className="subtasks">
                                {
                                    completedTasks.length ? completedTasks.map(task => (
                                        <Task
                                            key={task.id}
                                            id={task.id}
                                            name={task.name}
                                            completed={task.completed}
                                            due_date={task.due_date}
                                            total_subtasks={task.total_subtasks}
                                            completed_on={task.completed_on}
                                            completed_by={task.completed_by}
                                            projectId={task.project}
                                            onClick={onTaskClick}
                                            onUncomplete={canEditTask && handleTaskUncomplete}
                                        />
                                    )) : (
                                        <p className="no-entries">{ t('noEntries.noCompletedTasks') }</p>
                                    )
                                }
                            </div>
                        )
                    }
                </div>
            </div>
        </React.Fragment>
    );
}

export default withTranslation(['task', 'common'])(MilestoneTasks);
